import i18next from 'i18next';
import { toast } from 'sonner';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

/** API `code` → reportBranding locale key (under `errors.`). */
const ERROR_CODE_KEYS = Object.freeze({
    REPORT_BRANDING_MIGRATION_REQUIRED: 'errors.migrationRequired',
    REPORT_BRANDING_STALE: 'errors.stale',
    REPORT_BRANDING_NOT_FOUND: 'errors.notFound',
    REPORT_BRANDING_TRANSACTION_REQUIRED: 'errors.transactionRequired',
    REPORT_BRANDING_OPERATION_CONFLICT: 'errors.operationConflict',
    REPORT_BRANDING_LOGO_UNAVAILABLE: 'errors.logoUnavailable',
    REPORT_BRANDING_LOGO_INVALID: 'errors.logoInvalid',
    REPORT_BRANDING_CLASSIFICATION_UNAVAILABLE: 'errors.classificationUnavailable',
    REPORT_CHROME_EDIT_SESSION_INVALID: 'errors.chromeEditSessionInvalid',
    REPORT_CHROME_PART_INVALID: 'errors.chromePartInvalid',
    REPORT_CHROME_SAVE_NOT_FOUND: 'errors.chromeSaveNotFound',
    REPORT_CHROME_SAVE_PREPARE_FAILED: 'errors.chromeSavePrepareFailed',
    REPORT_CHROME_SAVE_RECEIPT_FAILED: 'errors.chromeSaveReceiptFailed',
    LIMIT_FILE_SIZE: 'errors.logoTooLarge',
});

/** Legacy English API body strings → same locale keys when `code` is missing. */
const ERROR_MESSAGE_KEYS = Object.freeze({
    'Report branding migration is required': 'errors.migrationRequired',
    'Report branding version is stale': 'errors.stale',
    'Report branding does not exist': 'errors.notFound',
    'Report branding logo is unavailable': 'errors.logoUnavailable',
    'Selected document classification is unavailable': 'errors.classificationUnavailable',
    'Report chrome edit session is invalid or expired': 'errors.chromeEditSessionInvalid',
    'Report chrome part is invalid': 'errors.chromePartInvalid',
    'Report chrome save receipt was not found': 'errors.chromeSaveNotFound',
    'Logo file exceeds 10 MiB': 'errors.logoTooLarge',
    'Logo upload is invalid': 'errors.logoUploadInvalid',
});

function resolveErrorCode(error) {
    return error?.code
        || error?.response?.data?.code
        || error?.originalError?.response?.data?.code
        || error?.original?.response?.data?.code
        || null;
}

export class ReportBrandingIntegration {
    constructor(service) { this.service = service; }

    async get() { return this.#read(() => this.service.get()); }

    async createDesignerCapability(input) {
        return this.#read(() => this.service.createDesignerCapability(input));
    }
    async getChromeSaveReceipt(editSessionId, saveId) {
        return this.#read(() => this.service.getChromeSaveReceipt(editSessionId, saveId));
    }
    async previewChrome(input) {
        return this.#read(() => this.service.previewChrome(input));
    }

    async commitChrome(input) {
        return this.#mutate(
            () => this.service.commitChrome(input),
            'reportBranding:messages.chromeLayoutSaved',
            'Report chrome layout was published.',
        );
    }

    async resetChrome(input) {
        return this.#mutate(
            () => this.service.resetChrome(input),
            'reportBranding:messages.chromeLayoutReset',
            'Report chrome layout was reset to the default.',
        );
    }

    async update(input) {
        return this.#mutate(
            () => this.service.update(input),
            'reportBranding:messages.saved',
            'Report branding was saved.',
        );
    }

    async replaceLogo(file, expectedVersion) {
        return this.#mutate(
            () => this.service.replaceLogo(file, expectedVersion),
            'reportBranding:messages.logoUpdated',
            'Report logo was updated.',
        );
    }

    async deleteLogo(expectedVersion) {
        return this.#mutate(
            () => this.service.deleteLogo(expectedVersion),
            'reportBranding:messages.logoRemoved',
            'Report logo was removed.',
        );
    }

    async #read(operation) {
        try {
            const response = await operation();
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: this.#localizedError(error),
                code: resolveErrorCode(error),
            };
        }
    }

    async #mutate(operation, key, fallback) {
        try {
            const response = await operation();
            toast.success(i18next.t(key, { defaultValue: fallback }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this.#localizedError(error);
            toast.error(message);
            return { success: false, error: message, code: resolveErrorCode(error) };
        }
    }

    #localizedError(error) {
        const code = resolveErrorCode(error);
        const codeKey = code ? ERROR_CODE_KEYS[code] : null;
        if (codeKey) {
            return i18next.t(`reportBranding:${codeKey}`, {
                defaultValue: typeof error?.message === 'string' ? error.message : codeKey,
            });
        }

        const raw = (typeof error?.message === 'string' ? error.message : '').trim();
        const messageKey = raw ? ERROR_MESSAGE_KEYS[raw] : null;
        if (messageKey) {
            return i18next.t(`reportBranding:${messageKey}`, { defaultValue: raw });
        }

        return getIntegrationErrorMessage(error) || i18next.t('reportBranding:requestFailed', {
            defaultValue: 'The report branding request could not be completed.',
        });
    }
}
